import { Component, ErrorInfo, ReactNode } from 'react';
import Logger from '../../utils/logger';
import { DefaultErrorFallback } from './DefaultErrorFallback';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (error: Error, retry: () => void, reset: () => void) => ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  isolate?: boolean;
  level?: 'page' | 'section' | 'component';
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorId: string;
  retryCount: number;
}

export class ModernErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private retryTimeoutId: NodeJS.Timeout | null = null;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorId: '',
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    const errorId = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    return {
      hasError: true,
      error,
      errorId,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { onError, level = 'component' } = this.props;

    try {
      // Enhanced error logging with context
      Logger.error(`Error Boundary (${level}) caught an error:`, {
        error: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        errorId: this.state.errorId,
        retryCount: this.state.retryCount,
        props: this.props,
      });
    } catch (err) {
      Logger.error('Unable to serialize error in boundary', err);
    }

    // Call custom error handler if provided
    onError?.(error, errorInfo);
  }

  componentWillUnmount() {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
    }
  }

  reset = () => {
    this.setState({
      hasError: false,
      error: null,
      retryCount: 0,
    });
  };

  retry = () => {
    if (this.state.retryCount >= 3) {
      Logger.warn('Maximum retry attempts reached', {
        errorId: this.state.errorId,
      });
      return;
    }

    this.setState((prevState) => ({
      hasError: false,
      error: null,
      retryCount: prevState.retryCount + 1,
    }));
  };

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.retry, this.reset);
      }

      // Default fallback with retry functionality
      return (
        <DefaultErrorFallback
          error={this.state.error}
          retry={this.retry}
          retryCount={this.state.retryCount}
          level={this.props.level}
        />
      );
    }

    return this.props.children;
  }
}
